'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter, useSearchParams } from 'next/navigation';

import { registerProvider } from '@/lib/auth/actions';
import { useToast } from '@/components/hooks/use-toast';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RegisterProviderInput, RegisterProviderSchema } from './reg.shema';
import { flattenFormErrors } from '@/lib/form/errors';
import { FormErrorSummary } from '@/components/forms/FormsErrorSumary';
import { Checkbox } from '@/components/ui/checkbox';
import Link from 'next/link';
import { useState } from 'react';
import { Eye, EyeOff, Info } from 'lucide-react';
import PhoneInput from './phoneComponent';
import { Switch } from '@/components/ui/switch';
import { IconArrowLeft, IconFocus, IconMail } from '@tabler/icons-react';
import GoogleAuthButton from './GoogleAuthButton';

export default function RegisterProviderForm() {
  const router = useRouter();
  const { toast } = useToast();
  const searchParams = useSearchParams();

  const signupFlow = searchParams.get('flow');
  const [isLoading, setLoading] = useState(false);
  const [showEmailReg, setEmailReg] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const form = useForm({
    resolver: zodResolver(RegisterProviderSchema),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      country: 'HR',
      providerType: undefined,
      phone: '',
      password: '',
      confirmPassword: '',
      role: 'provider',
      companyName: '',
      companyOib: '',
      companyAddress: '',
      companyCountry: 'HR',
      companyCity: '',
      companyPostalCode: '',
      terms: undefined,
      taxId: null,
      city: '',
      isCompanyInVat: false,
    },
    mode: 'onBlur', // ili 'onChange'

    reValidateMode: 'onChange',
  });

  const providerType = form.watch('providerType');

  const onSubmit = async (values: RegisterProviderInput) => {
    // alert(JSON.stringify(values));
    setLoading(true);
    const res = await registerProvider({
      ...values,
      signupFlow: signupFlow === 'ticketing' ? 'ticketing' : undefined,
    });

    if (res?.error) {
      setLoading(false);
      toast.error(res.error);
      return;
    }
    setLoading(false);
    toast.success('Račun uspješno kreiran');
    window.location.replace('/dashboard');
  };
  const errors = flattenFormErrors(form.formState.errors);
  const val = form.watch('terms');
  const isCompanyInVat = form.watch('isCompanyInVat');

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className='mx-auto w-full max-w-4xl space-y-8 rounded-3xl bg-white p-6 md:p-10'>
      {/* HEADER */}

      <div className='flex items-start justify-between gap-6'>
        <div>
          <h1 className='text-3xl font-semibold tracking-tight text-black'>
            Kreirajte partner račun
          </h1>

          <p className='mt-2 text-sm text-black/55'>
            Registracija traje manje od dvije minute.
          </p>
        </div>

        <Button
          type='button'
          variant='ghost'
          onClick={() => router.back()}
          className='text-black/60'>
          <IconArrowLeft size={18} />
          Prijava
        </Button>
      </div>

      {/* <FormErrorSummary errors={errors} /> */}

      <GoogleAuthButton
        signupFlow={signupFlow === 'ticketing' ? 'ticketing' : undefined}
      />

      <div className='flex items-center gap-4'>
        <div className='h-px flex-1 bg-black/10' />

        <span className='text-xs text-black/40'>
          ili nastavite email adresom
        </span>

        <div className='h-px flex-1 bg-black/10' />
      </div>

      {/* ===================================================== */}
      {/* OSOBNI PODACI */}
      {/* ===================================================== */}
      {!showEmailReg ? (
        <div className='flex '>
          <div className='m-auto border hover:bg-blue-50 rounded-sm w-80 text-sm py-2 px-2'>
            <button
              type='button'
              className='flex  items-center w-full justify-between text-center gap-2'
              onClick={() => setEmailReg(true)}>
              <IconMail className='' strokeWidth={1} />{' '}
              <span className=' m-auto text-center'>Nastavi sa emailom</span>
            </button>
          </div>
        </div>
      ) : (
        <div className='space-y-5'>
          <div className='space-y-5'>
            <div>
              <h2 className='text-sm font-semibold uppercase tracking-wide text-black/60'>
                Osobni podaci
              </h2>
            </div>

            <div className='grid gap-4 md:grid-cols-2'>
              <div className='space-y-1'>
                <Input
                  placeholder='Ime'
                  className='h-11 rounded-xl border-black/10 shadow-none focus-visible:ring-0 focus-visible:border-[var(--color-meet-green-dark)]'
                  {...form.register('name')}
                />

                {form.formState.errors.name && (
                  <p className='text-xs text-red-500'>
                    {form.formState.errors.name.message}
                  </p>
                )}
              </div>

              <div className='space-y-1'>
                <Input
                  placeholder='Prezime'
                  className='h-11 rounded-xl border-black/10 shadow-none focus-visible:ring-0 focus-visible:border-[var(--color-meet-green-dark)]'
                  {...form.register('surname')}
                />

                {form.formState.errors.surname && (
                  <p className='text-xs text-red-500'>
                    {form.formState.errors.surname.message}
                  </p>
                )}
              </div>
            </div>

            <div className='grid gap-4 md:grid-cols-2'>
              <div className='space-y-1'>
                <Input
                  type='email'
                  id='register-email'
                  placeholder='Email adresa'
                  autoComplete='email'
                  className='h-11 rounded-xl border-black/10 shadow-none focus-visible:ring-0 focus-visible:border-[var(--color-meet-green-dark)]'
                  {...form.register('email')}
                />

                {form.formState.errors.email && (
                  <p className='text-xs text-red-500'>
                    {form.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div className='space-y-1'>
                <PhoneInput form={form} />
              </div>
            </div>

            <div className='grid gap-4 md:grid-cols-2'>
              <div className='space-y-1'>
                <Input
                  type='text'
                  id='register-city'
                  placeholder='City'
                  autoComplete='address-level2'
                  className='h-11 rounded-xl border-black/10 shadow-none focus-visible:ring-0 focus-visible:border-[var(--color-meet-green-dark)]'
                  {...form.register('city')}
                />

                {form.formState.errors.city && (
                  <p className='text-xs text-red-500'>
                    {form.formState.errors.city.message}
                  </p>
                )}
              </div>

              <div className='space-y-1'>
                <Input
                  disabled
                  className='h-11 rounded-xl border-black/10 bg-[#f7f7f7] shadow-none'
                  {...form.register('country')}
                />
              </div>
            </div>
          </div>

          {/* ===================================================== */}
          {/* SIGURNOST */}
          {/* ===================================================== */}

          <div className='space-y-5'>
            <div>
              <h2 className='text-sm font-semibold uppercase tracking-wide text-black/60'>
                Sigurnost
              </h2>
            </div>

            <div className='grid gap-4 md:grid-cols-2'>
              {/* PASSWORD */}

              <div className='space-y-1'>
                <div className='relative'>
                  <Input
                    placeholder='Lozinka'
                    type={showPassword ? 'text' : 'password'}
                    {...form.register('password')}
                    className='h-11 rounded-xl border-black/10 pr-10 shadow-none focus-visible:ring-0 focus-visible:border-[var(--color-meet-green-dark)]'
                  />

                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    onClick={() => setShowPassword((v) => !v)}
                    className='absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2'>
                    {showPassword ? (
                      <EyeOff className='h-4 w-4' />
                    ) : (
                      <Eye className='h-4 w-4' />
                    )}
                  </Button>
                </div>

                {form.formState.errors.password && (
                  <p className='text-xs text-red-500'>
                    {form.formState.errors.password.message}
                  </p>
                )}
              </div>

              {/* CONFIRM */}

              <div className='space-y-1'>
                <div className='relative'>
                  <Input
                    placeholder='Potvrdite lozinku'
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...form.register('confirmPassword')}
                    className='h-11 rounded-xl border-black/10 pr-10 shadow-none focus-visible:ring-0 focus-visible:border-[var(--color-meet-green-dark)]'
                  />

                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className='absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2'>
                    {showConfirmPassword ? (
                      <EyeOff className='h-4 w-4' />
                    ) : (
                      <Eye className='h-4 w-4' />
                    )}
                  </Button>
                </div>

                {form.formState.errors.confirmPassword && (
                  <p className='text-xs text-red-500'>
                    {form.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* OVDJE OSTAVI PROVIDER TYPE */}
          {/* ===================================================== */}
          {/* PARTNER */}
          {/* ===================================================== */}

          <div className='space-y-5'>
            <div>
              <h2 className='text-sm font-semibold uppercase tracking-wide text-black/60'>
                Partner račun
              </h2>
            </div>

            <div className='grid grid-cols-2 gap-3'>
              <label className='cursor-pointer'>
                <input
                  type='radio'
                  value='private'
                  {...form.register('providerType')}
                  className='peer sr-only'
                />

                <div className='flex h-14 items-center justify-center rounded-xl border border-black/10 bg-white font-medium transition-all peer-checked:border-[var(--color-meet-green-dark)] peer-checked:bg-[var(--color-meet-green)]/10'>
                  Privatna osoba
                </div>
              </label>

              <label className='cursor-pointer'>
                <input
                  type='radio'
                  value='company'
                  {...form.register('providerType')}
                  className='peer sr-only'
                />

                <div className='flex h-14 items-center justify-center rounded-xl border border-black/10 bg-white font-medium transition-all peer-checked:border-[var(--color-meet-green-dark)] peer-checked:bg-[var(--color-meet-green)]/10'>
                  Tvrtka
                </div>
              </label>
            </div>

            {form.formState.errors.providerType && (
              <p className='text-xs text-red-500'>
                {form.formState.errors.providerType.message}
              </p>
            )}
          </div>

          {/* ===================================================== */}
          {/* TVRTKA */}
          {/* ===================================================== */}

          {providerType === 'company' && (
            <div className='space-y-6 border-t border-black/10 pt-8'>
              <div>
                <h3 className='font-semibold'>Podaci o tvrtki</h3>

                <p className='mt-1 text-sm text-black/55'>
                  Ovi podaci koriste se za identifikaciju partnera i izdavanje
                  računa.
                </p>
              </div>

              <div className='grid gap-4 md:grid-cols-2'>
                <Input
                  placeholder='Naziv tvrtke'
                  className='h-11 rounded-xl border-black/10 shadow-none'
                  {...form.register('companyName')}
                />

                <Input
                  placeholder='Adresa'
                  className='h-11 rounded-xl border-black/10 shadow-none'
                  {...form.register('companyAddress')}
                />

                <Input
                  placeholder='OIB'
                  className='h-11 rounded-xl border-black/10 shadow-none'
                  {...form.register('companyOib')}
                />

                <Input
                  placeholder='Poštanski broj'
                  className='h-11 rounded-xl border-black/10 shadow-none'
                  {...form.register('companyPostalCode')}
                />

                <Input
                  placeholder='Grad'
                  className='h-11 rounded-xl border-black/10 shadow-none'
                  {...form.register('companyCity')}
                />

                <Input
                  disabled
                  className='h-11 rounded-xl border-black/10 bg-[#f7f7f7]'
                  {...form.register('companyCountry')}
                />
              </div>

              <div className='rounded-2xl border border-black/10 p-5'>
                <div className='flex items-center justify-between'>
                  <div>
                    <p className='font-medium'>Tvrtka je u sustavu PDV-a</p>

                    <p className='mt-1 text-sm text-black/55'>
                      Ako ste u sustavu PDV-a unesite VAT broj.
                    </p>
                  </div>

                  <Switch
                    checked={isCompanyInVat}
                    onCheckedChange={(val) => {
                      form.setValue('isCompanyInVat', val, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });

                      if (!val) {
                        form.setValue('taxId', null, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });

                        form.clearErrors('taxId');
                      }
                    }}
                  />
                </div>

                {isCompanyInVat && (
                  <div className='mt-5'>
                    <Input
                      placeholder='HR12345678901'
                      className='h-11 rounded-xl border-black/10 shadow-none'
                      {...form.register('taxId')}
                    />

                    {form.formState.errors.taxId && (
                      <p className='mt-2 text-xs text-red-500'>
                        {form.formState.errors.taxId.message}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===================================================== */}
          {/* UVJETI */}
          {/* ===================================================== */}

          <div className='border-t border-black/10 pt-6'>
            <label className='flex items-start gap-3'>
              <input
                type='checkbox'
                {...form.register('terms')}
                className='mt-1 h-4 w-4 accent-[var(--color-meet-green-dark)]'
              />

              <span className='text-sm leading-6 text-black/70'>
                Prihvaćam{' '}
                <a
                  href='/legal/terms-of-use'
                  target='_blank'
                  className='font-medium text-[var(--color-meet-green-dark)] hover:underline'>
                  uvjete korištenja
                </a>{' '}
                i potvrđujem točnost unesenih podataka.
              </span>
            </label>

            {form.formState.errors.terms && (
              <p className='mt-2 text-xs text-red-500'>
                {form.formState.errors.terms.message}
              </p>
            )}
          </div>

          {/* ===================================================== */}
          {/* SUBMIT */}
          {/* ===================================================== */}

          <Button
            type='submit'
            disabled={isLoading}
            className='h-12 w-full rounded-xl bg-[var(--color-meet-green-dark)] text-white hover:bg-[var(--color-meet-green-dark)]/90'>
            <IconFocus className='mr-2 h-4 w-4' />

            {isLoading ? 'Kreiram partner račun...' : 'Kreiraj partner račun'}
          </Button>

          <p className='text-center text-sm text-black/55'>
            Već imate partner račun?{' '}
            <Link
              href='/auth/login'
              className='font-medium text-[var(--color-meet-green-dark)] hover:underline'>
              Prijavite se
            </Link>
          </p>
        </div>
      )}
    </form>
  );
}
